import { isAfterHours, hoursFromRows } from './businessHours.js';

export const AI_ASSISTANT_DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
export const AI_ASSISTANT_WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const AI_PAUSE_UNTIL_CLOSED_REASON = 'staff_takeover_until_closed';

export const DEFAULT_AI_ASSISTANT_SCHEDULE = Object.freeze(
    Object.fromEntries(AI_ASSISTANT_WEEKDAYS.map((day) => [day, [{ start: '00:00', end: '06:00' }]]))
);

export const DEFAULT_AI_ASSISTANT_SETTINGS = Object.freeze({
    mode: 'on',
    timezone: 'America/New_York',
    schedule: DEFAULT_AI_ASSISTANT_SCHEDULE,
    holidays: [],
    channels: { sms: true, whatsapp: true, email: true },
    takeover_pause_minutes: 240,
    emergency_paused_until: null,
    conversation_idle_minutes: 30,
});

// Exactly the org_ai_assistant_settings columns the Communications page reads. Real table columns only (checked against the
// migrations by aiAssistantSettingsColumns.test.js): the pauser's NAME is computed by ai_integration_get, never selected here.
export const AI_ASSISTANT_SETTINGS_COLUMNS = 'org_id, mode, status, cover_after_hours, timezone, schedule, holidays, channels, takeover_pause_minutes, emergency_paused_until, emergency_paused_by, emergency_paused_at, conversation_idle_minutes';

// BL-177 Step 2m: the status model every AI feature uses. Off = never replies; On = replies (around the clock, or only outside the
// open hours when cover_after_hours); Pause = stops at once, settings kept; Test = replies ONLY to the test list.
export const AI_STATUSES = Object.freeze(['off', 'on', 'pause', 'test']);

export function describeAiAssistantStatus(settings, now = new Date()) {
    const until = validDate(settings?.emergency_paused_until);
    if (until && until > now) {
        return { active: false, tone: 'orange', label: `AI: Paused until ${until.getUTCFullYear() >= 9999 ? 'resumed' : until.toLocaleString()}` };
    }
    const status = String(settings?.status || 'off').toLowerCase();
    if (status === 'pause') return { active: false, tone: 'orange', label: 'AI: Paused until resumed' };
    if (status === 'test') return { active: true, tone: 'orange', label: 'AI: Test' };
    if (status !== 'on') return { active: false, tone: 'grey', label: 'AI: Off' };
    const gate = evaluateAiAssistantGate({ settings, channel: 'sms', now });
    if (!gate.active && settings.after_hours_now === false) {
        return { active: false, tone: 'orange', label: 'AI off · staff hours' };
    }
    return {
        active: gate.active,
        tone: gate.active ? 'green' : 'orange',
        label: `AI: On${settings.cover_after_hours !== false ? ' · after hours' : ''}`,
    };
}

export function describeAiThreadStatus({ settings, session = {}, pausedBy = 'Staff', until = '', now = new Date() } = {}) {
    const verdict = evaluateAiAssistantGate({
        settings: settings || AI_OFF_SETTINGS, channel: session.channel, session, now,
        contactAddress: session.contact_phone || session.contact_email,
    });
    if (verdict.active) return null;
    if (verdict.reason.includes('staff takeover')) return { label: `AI paused · by ${pausedBy} until ${until || 'this thread closes'}`, explanation: `Staff took over this conversation. AI resumes ${until ? `at ${until}` : 'when this thread closes'}.`, paused: true };
    if (verdict.reason.includes('test list')) return { label: 'AI off · not on test list', explanation: 'AI is in Test mode. This contact is not on the company’s AI Test list.', paused: false };
    if (verdict.reason === 'open hours: staff are covering') return { label: 'AI off · staff hours', explanation: 'Staff are covering these hours. AI is configured to reply only after hours.', paused: false };
    if (verdict.reason === 'paused everywhere' || verdict.reason === 'AI is paused') return { label: 'AI paused · company-wide', explanation: 'AI is paused for the company in Business Schedule.', paused: true };
    return { label: 'AI off', explanation: verdict.reason, paused: false };
}

/**
 * Polish C2: the ONE label the conversation header shows. Display only — it never decides whether the AI answers.
 * Precedence: (1) Test mode keeps its existing labels exactly; (2) company AI not running right now — switched off, no saved status,
 * this channel off, or it covers only after hours and staff hours are on now — reads "AI: Off" (same words as the Communications
 * header chip); (3) company-wide pause reads "AI paused · company-wide"; (4) AI on and running but this thread taken over by staff
 * reads "AI paused · by … until …". A staff pause on a thread whose AI is off is kept in `threadPaused` so the details can mention it.
 */
export function describeConversationAiStatus({ settings, session = {}, pausedByName = 'Staff', resumeLabel = '', now = new Date() } = {}) {
    const current = validDate(now) || new Date();
    const pauseUntil = validDate(session?.ai_paused_until);
    const threadPaused = Boolean(pauseUntil && pauseUntil > current)
        || (!pauseUntil && session?.ai_pause_reason === AI_PAUSE_UNTIL_CLOSED_REASON && isAiThreadOpen(session));
    const pausedLabel = `AI paused · by ${pausedByName} until ${resumeLabel || 'resumed'}`;
    const status = String(settings?.status || 'off').toLowerCase();
    const emergency = validDate(settings?.emergency_paused_until);
    const companyPaused = Boolean(emergency && emergency > current) || status === 'pause';

    // (1) Test mode: unchanged from before this change.
    if (status === 'test' && !companyPaused) {
        if (threadPaused) return { label: pausedLabel, explanation: `This conversation is paused by ${pausedByName}.`, paused: true, state: 'thread-paused', threadPaused };
        const verdict = describeAiThreadStatus({ settings, session, now: current });
        return verdict && { ...verdict, state: verdict.paused ? 'thread-paused' : 'test', threadPaused };
    }
    if (companyPaused) {
        return { label: 'AI paused · company-wide', explanation: 'AI is paused for the company in Business Schedule.', paused: true, state: 'company-paused', threadPaused };
    }
    const off = (explanation) => ({ label: 'AI: Off', explanation, paused: false, state: 'off', threadPaused });
    if (status !== 'on') return off('The company’s AI is switched off in Business Schedule.');
    const channel = normalizeAiAssistantChannel(session?.channel);
    const channels = settings?.channels || DEFAULT_AI_ASSISTANT_SETTINGS.channels;
    if (channel && channels[channel] === false) return off(`The AI is switched off for ${channel === 'sms' ? 'SMS' : channel === 'whatsapp' ? 'WhatsApp' : channel}.`);
    if (settings?.cover_after_hours !== false && settings?.after_hours_now === false) {
        return off('Staff are covering these hours. AI is set to reply only after hours.');
    }
    // (4) The AI would be answering right now (or the hours could not be checked): a staff takeover is a real pause.
    if (threadPaused) return { label: pausedLabel, explanation: `This conversation is paused by ${pausedByName}.`, paused: true, state: 'thread-paused', threadPaused };
    const verdict = describeAiThreadStatus({ settings, session, now: current });
    return verdict && { ...verdict, state: 'off', threadPaused };
}

// What to assume when a company has no settings row (or the platform secret is missing): OFF. Nobody gets an AI reply by default.
export const AI_OFF_SETTINGS = Object.freeze({
    ...DEFAULT_AI_ASSISTANT_SETTINGS,
    status: 'off',
    mode: 'off',
    cover_after_hours: true,
    assistant_name: '',
});

/**
 * One address in the one form the test list is compared in: a phone becomes E.164 (+1 assumed for 10 digits), an email is lower-cased,
 * and a 'whatsapp:' prefix is ignored. Returns '' for anything that is neither. (Mirrors comms_norm_address in the database.)
 */
export function normalizeTestAddress(value) {
    const raw = String(value ?? '').trim().replace(/^whatsapp:\s*/i, '');
    if (!raw) return '';
    if (raw.includes('@')) return raw.toLowerCase();
    const digits = raw.replace(/\D/g, '');
    if (/^\+[1-9]\d{7,14}$/.test(raw)) return raw;
    if (/^[2-9]\d{9}$/.test(digits) && !raw.startsWith('+')) return `+1${digits}`;
    if (/^1[2-9]\d{9}$/.test(digits)) return `+${digits}`;
    if (raw.startsWith('+') && /^[1-9]\d{7,14}$/.test(digits)) return `+${digits}`;
    return '';
}

/** True only when the address is on the list ([{kind, value}] or plain strings). Empty address or empty list: false. */
export function testListAllows(testContacts, address) {
    const wanted = normalizeTestAddress(address);
    if (!wanted || !Array.isArray(testContacts)) return false;
    return testContacts.some((entry) => normalizeTestAddress(typeof entry === 'string' ? entry : entry?.value) === wanted);
}

const TERMINAL_APPROVAL_STATUSES = new Set(['approved', 'declined', 'resolved', 'closed']);
const TERMINAL_OUTCOMES = new Set(['resolved', 'auto_confirmed', 'escalated', 'abandoned']);

function validDate(value) {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function localParts(now, timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
    return {
        day: values.weekday.toLowerCase().slice(0, 3),
        date: `${values.year}-${values.month}-${values.day}`,
        minute: Number(values.hour) * 60 + Number(values.minute),
    };
}

function minuteOfDay(value) {
    if (typeof value !== 'string' || !/^\d{2}:\d{2}$/.test(value)) return null;
    const [hour, minute] = value.split(':').map(Number);
    if (hour > 23 || minute > 59) return null;
    return hour * 60 + minute;
}

function windowContainsMinute(window, minute) {
    const start = minuteOfDay(window?.start);
    const end = minuteOfDay(window?.end);
    if (start === null || end === null) return false;
    if (start === end) return true;
    return start < end
        ? minute >= start && minute < end
        : minute >= start;
}

function previousDay(day) {
    const index = AI_ASSISTANT_DAYS.indexOf(day);
    return AI_ASSISTANT_DAYS[(index + 6) % 7];
}

function scheduleContainsLocalTime(schedule, local) {
    const todayWindows = Array.isArray(schedule?.[local.day]) ? schedule[local.day] : [];
    if (todayWindows.some((window) => windowContainsMinute(window, local.minute))) return true;

    const priorWindows = Array.isArray(schedule?.[previousDay(local.day)])
        ? schedule[previousDay(local.day)]
        : [];
    return priorWindows.some((window) => {
        const start = minuteOfDay(window?.start);
        const end = minuteOfDay(window?.end);
        return start !== null && end !== null && start > end && local.minute < end;
    });
}

export function isAiThreadOpen(session = {}) {
    const approvalStatus = String(session?.approval_status || '').trim().toLowerCase();
    const outcome = String(session?.outcome || '').trim().toLowerCase();
    return !TERMINAL_APPROVAL_STATUSES.has(approvalStatus) && !TERMINAL_OUTCOMES.has(outcome);
}

export function normalizeAiAssistantChannel(channel) {
    const normalized = String(channel || '').trim().toLowerCase();
    return normalized === 'mms' ? 'sms' : normalized;
}

/** Step 2m: the status-based decision. Order: pause everywhere (caller), status, channel, test list, staff takeover, open hours. */
function evaluateByStatus({ settings, channel, session, currentTime, testContacts, contactAddress, afterHoursNow }) {
    const status = String(settings.status).toLowerCase();
    if (status === 'off') return { active: false, reason: 'AI is off' };
    if (status === 'pause') return { active: false, reason: 'AI is paused' };
    if (status !== 'on' && status !== 'test') return { active: false, reason: `unsupported status: ${status}` };

    const normalizedChannel = normalizeAiAssistantChannel(channel);
    const channels = settings?.channels || DEFAULT_AI_ASSISTANT_SETTINGS.channels;
    if (!normalizedChannel || channels[normalizedChannel] !== true) {
        return { active: false, reason: `${normalizedChannel || 'unknown'} channel is off` };
    }

    // TEST: the AI answers ONLY the listed phones / emails, on every channel. Everyone else gets nothing.
    if (status === 'test' && !testListAllows(testContacts, contactAddress)) {
        return { active: false, reason: 'test mode: this sender is not on the test list' };
    }

    const sessionPause = validDate(session?.ai_paused_until);
    if (sessionPause && sessionPause > currentTime) {
        return { active: false, reason: 'staff takeover pause is active' };
    }
    if (!sessionPause && session?.ai_pause_reason === AI_PAUSE_UNTIL_CLOSED_REASON && isAiThreadOpen(session)) {
        return { active: false, reason: 'staff takeover lasts until thread closes' };
    }

    if (status === 'test') return { active: true, reason: 'test mode: listed sender' };
    if (settings.cover_after_hours === false) return { active: true, reason: 'on all the time' };

    // Cover after-hours: answer only while the business is closed. The server asks the database (is_org_after_hours) and passes
    // the answer in; a screen can pass the saved open hours instead. If neither is known the AI stays quiet.
    let closed = typeof afterHoursNow === 'boolean' ? afterHoursNow : null;
    if (closed === null && settings.business) {
        try {
            const holidays = Array.isArray(settings.business.holidays) ? settings.business.holidays : [];
            const zone = settings.business.time_zone || DEFAULT_AI_ASSISTANT_SETTINGS.timezone;
            const today = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(currentTime);
            closed = holidays.includes(today) || isAfterHours(hoursFromRows(settings.business.hours), zone, currentTime);
        } catch {
            return { active: false, reason: 'invalid organization timezone' };
        }
    }
    if (closed === null) return { active: false, reason: 'open hours are not available' };
    return closed
        ? { active: true, reason: 'after hours: staff are closed' }
        : { active: false, reason: 'open hours: staff are covering' };
}

/**
 * @param {{
 *   settings?: {
 *     mode?: string,
 *     status?: string,
 *     cover_after_hours?: boolean,
 *     after_hours_now?: boolean,
 *     test_contacts?: Array<{kind?: string, value?: string}>,
 *     business?: {time_zone?: string, holidays?: string[], hours?: Array<Record<string, unknown>>},
 *     timezone?: string,
 *     schedule?: Record<string, Array<{start?: string, end?: string}>>,
 *     holidays?: string[],
 *     channels?: Record<string, boolean>,
 *     takeover_pause_minutes?: number | null,
 *     emergency_paused_until?: string | Date | null
 *   },
 *   channel?: string,
 *   session?: {
 *     ai_paused_until?: string | Date | null,
 *     ai_pause_reason?: string | null,
 *     approval_status?: string | null,
 *     outcome?: string | null
 *   },
 *   now?: Date,
 *   platformSecretPresent?: boolean,
 *   testContacts?: Array<{kind?: string, value?: string}>,
 *   contactAddress?: string | null,
 *   afterHoursNow?: boolean
 * }} [options]
 */
export function evaluateAiAssistantGate({
    settings = DEFAULT_AI_ASSISTANT_SETTINGS,
    channel,
    session = {},
    now = new Date(),
    platformSecretPresent = true,
    testContacts = settings?.test_contacts,
    contactAddress,
    afterHoursNow = settings?.after_hours_now,
} = {}) {
    const currentTime = validDate(now);
    if (!currentTime) return { active: false, reason: 'invalid current time' };
    if (!platformSecretPresent) return { active: false, reason: 'platform secret missing' };

    const emergencyPause = validDate(settings?.emergency_paused_until);
    if (emergencyPause && emergencyPause > currentTime) {
        return { active: false, reason: 'paused everywhere' };
    }

    // Step 2m settings carry a `status`; anything older (no status) keeps the old mode logic unchanged.
    if (settings?.status !== undefined && settings?.status !== null) {
        return evaluateByStatus({ settings, channel, session, currentTime, testContacts, contactAddress, afterHoursNow });
    }

    const mode = String(settings?.mode || DEFAULT_AI_ASSISTANT_SETTINGS.mode).toLowerCase();
    if (mode === 'off') return { active: false, reason: 'organization mode is off' };

    const normalizedChannel = normalizeAiAssistantChannel(channel);
    const channels = settings?.channels || DEFAULT_AI_ASSISTANT_SETTINGS.channels;
    if (!normalizedChannel || channels[normalizedChannel] !== true) {
        return { active: false, reason: `${normalizedChannel || 'unknown'} channel is off` };
    }

    const sessionPause = validDate(session?.ai_paused_until);
    if (sessionPause && sessionPause > currentTime) {
        return { active: false, reason: 'staff takeover pause is active' };
    }
    if (
        !sessionPause
        && session?.ai_pause_reason === AI_PAUSE_UNTIL_CLOSED_REASON
        && isAiThreadOpen(session)
    ) {
        return { active: false, reason: 'staff takeover lasts until thread closes' };
    }

    if (mode === 'on') return { active: true, reason: 'on all the time' };
    if (mode !== 'scheduled') return { active: false, reason: `unsupported mode: ${mode}` };

    let local;
    try {
        local = localParts(currentTime, settings?.timezone || DEFAULT_AI_ASSISTANT_SETTINGS.timezone);
    } catch {
        return { active: false, reason: 'invalid organization timezone' };
    }

    const holidays = Array.isArray(settings?.holidays) ? settings.holidays : [];
    if (holidays.includes(local.date)) return { active: true, reason: 'scheduled holiday coverage' };

    if (scheduleContainsLocalTime(settings?.schedule || DEFAULT_AI_ASSISTANT_SETTINGS.schedule, local)) {
        return { active: true, reason: 'inside scheduled answering window' };
    }
    return { active: false, reason: 'outside scheduled answering window' };
}
